import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Plus, UserCircle, Trash, Key } from "@phosphor-icons/react";
import { useAuth } from "@/contexts/AuthContext";

export default function TeamDialog({ open, onClose }) {
  const { user: me } = useAuth();
  const [users, setUsers] = useState([]);
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("91");
  const [full_name, setFullName] = useState("");
  const [role, setRole] = useState("agent");
  const [saving, setSaving] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const load = () => api.get("/auth/users").then((r) => setUsers(r.data)).catch(() => {});
  useEffect(() => { if (open) load(); }, [open]);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const cleanPhone = phone.replace(/\D/g, "");
      await api.post("/auth/register", {
        phone: cleanPhone,
        email: email || null,
        full_name,
        role,
      });
      toast.success("Team member added — they can now login via OTP");
      setEmail(""); setPhone("91"); setFullName(""); setRole("agent");
      load();
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : JSON.stringify(d) || "Failed");
    } finally { setSaving(false); }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    try {
      await api.delete(`/auth/users/${deleteTarget.id}`);
      toast.success(`${deleteTarget.full_name || deleteTarget.email} removed`);
      setDeleteTarget(null);
      load();
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Delete failed");
    }
  };

  return (
    <>
      <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
        <DialogContent className="max-w-xl max-h-[92vh] overflow-y-auto" data-testid="team-dialog">
          <DialogHeader>
            <DialogTitle className="heading">Team members</DialogTitle>
            <DialogDescription>
              Admins can add/remove members and change passwords.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-5">
            <div className="border border-[var(--border)] rounded-lg divide-y divide-[var(--border)] max-h-64 overflow-y-auto scroll-thin">
              {users.map((u) => {
                const isMe = u.id === me?.id;
                return (
                  <div key={u.id} className="flex items-center gap-3 px-3 py-2.5 text-sm" data-testid={`user-row-${u.id}`}>
                    <UserCircle size={22} className="text-[var(--muted)]" />
                    <div className="flex-1 min-w-0">
                      <div className="font-medium truncate">
                        {u.full_name || u.phone || u.email}
                        {isMe && <span className="ml-2 text-[10px] text-[var(--muted)]">(you)</span>}
                      </div>
                      <div className="text-[11px] text-[var(--muted)] truncate">
                        {u.phone ? `+${u.phone}` : ""}{u.phone && u.email ? " · " : ""}{u.email || ""}
                      </div>
                    </div>
                    <span className="text-[10px] px-2 py-0.5 rounded-full bg-[var(--bg)] uppercase tracking-wider">
                      {u.role}
                    </span>

                    <PasswordPopover user={u} onDone={load} canEdit={me?.role === "admin" || isMe} />

                    {me?.role === "admin" && !isMe && (
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-7 w-7 text-[#EF4444]"
                        onClick={() => setDeleteTarget(u)}
                        data-testid={`delete-user-${u.id}`}
                        title="Remove user"
                      >
                        <Trash size={14} />
                      </Button>
                    )}
                  </div>
                );
              })}
              {users.length === 0 && (
                <div className="p-4 text-xs text-[var(--muted)] text-center">No team members yet.</div>
              )}
            </div>

            {me?.role === "admin" && (
              <form onSubmit={submit} className="grid grid-cols-1 sm:grid-cols-2 gap-3 border-t border-[var(--border)] pt-4" data-testid="add-user-form">
                <div className="col-span-1 sm:col-span-2 text-xs font-semibold heading">Add new member</div>
                <div className="space-y-1.5">
                  <Label className="text-xs">Full name</Label>
                  <Input required value={full_name} onChange={(e) => setFullName(e.target.value)} data-testid="new-user-name" />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs">Phone (10-digit mobile)</Label>
                  <div className="flex gap-2 items-stretch">
                    <div className="flex items-center px-3 rounded-md border border-[var(--border)] bg-[var(--bg)] text-xs text-[var(--muted)] font-mono">
                      +91
                    </div>
                    <Input
                      required
                      type="tel"
                      inputMode="numeric"
                      placeholder="98XX XXXXXX"
                      value={phone.startsWith("91") ? phone.slice(2) : phone}
                      onChange={(e) => {
                        const d = e.target.value.replace(/\D/g, "").slice(0, 10);
                        setPhone("91" + d);
                      }}
                      className="flex-1"
                      data-testid="new-user-phone"
                    />
                  </div>
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs">Email (optional)</Label>
                  <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="new-user-email" />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs">Role</Label>
                  <Select value={role} onValueChange={setRole}>
                    <SelectTrigger data-testid="new-user-role">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="agent">Agent</SelectItem>
                      <SelectItem value="admin">Admin</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="col-span-1 sm:col-span-2 text-[11px] text-[var(--muted)] bg-[var(--bg)] p-2 rounded">
                  🔐 Member will login via OTP sent to their WhatsApp. No password needed.
                </div>
                <div className="col-span-1 sm:col-span-2">
                  <Button type="submit" disabled={saving} className="w-full gap-1.5" data-testid="add-user-submit">
                    <Plus size={14} weight="bold" />
                    {saving ? "Adding…" : "Add member"}
                  </Button>
                </div>
              </form>
            )}
          </div>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleteTarget} onOpenChange={(v) => !v && setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove this user?</AlertDialogTitle>
            <AlertDialogDescription>
              {deleteTarget?.full_name || deleteTarget?.email} will lose access immediately.
              This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} className="bg-[#EF4444] hover:bg-[#DC2626]" data-testid="confirm-delete-user">
              Remove user
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}

function PasswordPopover({ user, onDone, canEdit }) {
  const [open, setOpen] = useState(false);
  const [pw, setPw] = useState("");
  const [busy, setBusy] = useState(false);
  if (!canEdit) return null;
  const submit = async (e) => {
    e.preventDefault();
    if (pw.length < 6) return toast.error("Min 6 characters");
    setBusy(true);
    try {
      await api.patch(`/auth/users/${user.id}/password`, { password: pw });
      toast.success("Password updated");
      setPw("");
      setOpen(false);
      onDone();
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Failed");
    } finally { setBusy(false); }
  };
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="ghost" size="icon" className="h-7 w-7" title="Change password" data-testid={`change-pw-${user.id}`}>
          <Key size={14} />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-64" align="end">
        <form onSubmit={submit} className="space-y-2">
          <Label className="text-xs">
            New password for <b>{user.full_name || user.email}</b>
          </Label>
          <Input
            type="password"
            placeholder="Min 6 characters"
            value={pw}
            onChange={(e) => setPw(e.target.value)}
            autoFocus
            data-testid={`pw-input-${user.id}`}
          />
          <Button type="submit" size="sm" className="w-full" disabled={busy} data-testid={`pw-save-${user.id}`}>
            {busy ? "Saving…" : "Update password"}
          </Button>
        </form>
      </PopoverContent>
    </Popover>
  );
}
